import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import type { InsertTask, Task, TaskComment, TaskPiece } from "@shared/schema";
import { authFetch } from "@/lib/authFetch";

export function useTasks() {
  return useQuery<Task[]>({
    queryKey: ["/api/tasks"],
    queryFn: async () => {
      const res = await authFetch("/api/tasks");
      if (!res.ok) throw new Error("Failed to fetch tasks");
      return res.json();
    },
  });
}

export function useCreateTask() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: InsertTask) => {
      const res = await authFetch("/api/tasks", {
        method: "POST",
        body: JSON.stringify(data),
      });
      if (!res.ok) throw new Error("Failed to create task");
      return res.json();
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["/api/tasks"] }),
  });
}

export function useUpdateTask() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...updates }: { id: string } & Partial<InsertTask>) => {
      const res = await authFetch(`/api/tasks/${id}`, {
        method: "PUT",
        body: JSON.stringify(updates),
      });
      if (!res.ok) throw new Error("Failed to update task");
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/tasks"] });
      queryClient.invalidateQueries({ queryKey: ["/api/tasks/echeance-changes"] });
    },
  });
}

export function useDeleteTask() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const res = await authFetch(`/api/tasks/${id}`, { method: "DELETE" });
      if (!res.ok) throw new Error("Failed to delete task");
      return res.json();
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["/api/tasks"] }),
  });
}

export function useArchiveTask() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const res = await authFetch(`/api/tasks/${id}/archive`, { method: "PUT" });
      if (!res.ok) throw new Error("Failed to archive task");
      return res.json();
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["/api/tasks"] }),
  });
}

export function useUnarchiveTask() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const res = await authFetch(`/api/tasks/${id}/unarchive`, { method: "PUT" });
      if (!res.ok) throw new Error("Failed to unarchive task");
      return res.json();
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["/api/tasks"] }),
  });
}

export function useTaskEcheanceChanges() {
  return useQuery<Record<string, { count: number; lastContenu: string; lastDate: string; history: { contenu: string; date: string }[] }>>({
    queryKey: ["/api/tasks/echeance-changes"],
    queryFn: async () => {
      const res = await authFetch("/api/tasks/echeance-changes");
      if (!res.ok) throw new Error("Failed to fetch echeance changes");
      return res.json();
    },
    staleTime: 30_000,
  });
}

export function useTaskComments(taskId: string | null) {
  return useQuery<TaskComment[]>({
    queryKey: ["/api/tasks", taskId, "comments"],
    queryFn: async () => {
      const res = await authFetch(`/api/tasks/${taskId}/comments`);
      if (!res.ok) throw new Error("Failed to fetch comments");
      return res.json();
    },
    enabled: !!taskId,
  });
}

export function useCreateTaskComment() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ taskId, contenu, pieceJointeUrl }: { taskId: string; contenu: string; pieceJointeUrl?: string | null }) => {
      const res = await authFetch(`/api/tasks/${taskId}/comments`, {
        method: "POST",
        body: JSON.stringify({ contenu, pieceJointeUrl }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err?.message || `Erreur ${res.status}`);
      }
      return res.json();
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ["/api/tasks", variables.taskId, "comments"] });
    },
  });
}

export function useDeleteTaskComment() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, taskId }: { id: string; taskId: string }) => {
      const res = await authFetch(`/api/task-comments/${id}`, { method: "DELETE" });
      if (!res.ok) throw new Error("Failed to delete comment");
      return res.json();
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ["/api/tasks", variables.taskId, "comments"] });
    },
  });
}

export function useTaskPieces(taskId: string | null) {
  return useQuery<TaskPiece[]>({
    queryKey: ["/api/tasks", taskId, "pieces"],
    queryFn: async () => {
      const res = await authFetch(`/api/tasks/${taskId}/pieces`);
      if (!res.ok) throw new Error("Failed to fetch task pieces");
      return res.json();
    },
    enabled: !!taskId,
  });
}

export function useCreateTaskPiece() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ taskId, produitId, note }: { taskId: string; produitId: string; note?: string | null }) => {
      const res = await authFetch(`/api/tasks/${taskId}/pieces`, {
        method: "POST",
        body: JSON.stringify({ produitId, note }),
      });
      if (!res.ok) throw new Error("Failed to add piece");
      return res.json();
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ["/api/tasks", variables.taskId, "pieces"] });
    },
  });
}

export function useDeleteTaskPiece() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, taskId }: { id: string; taskId: string }) => {
      const res = await authFetch(`/api/task-pieces/${id}`, { method: "DELETE" });
      if (!res.ok) throw new Error("Failed to delete piece");
      return res.json();
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ["/api/tasks", variables.taskId, "pieces"] });
    },
  });
}

export function useTasksByEmetteur(emetteurType: string | null, emetteurId: string | null) {
  return useQuery<Task[]>({
    queryKey: ["/api/tasks-by-emetteur", emetteurType, emetteurId],
    queryFn: async () => {
      const res = await authFetch(`/api/tasks-by-emetteur?emetteurType=${emetteurType}&emetteurId=${emetteurId}`);
      if (!res.ok) throw new Error("Failed to fetch tasks by emetteur");
      return res.json();
    },
    enabled: !!emetteurType && !!emetteurId,
  });
}
